import * as Yup from 'yup';

import type Address from '../models/Address';
import type CourierService from '../models/CourierService';
import type RefundLineItem from '../models/RefundLineItem';

import DeliveryMode from '../models/DeliveryMode';

import dayjs from 'dayjs';
import { TFunction } from 'react-i18next';
import { PATTERNS } from 'utils/constans/constans';
import DELIVERY_MODES from '../constants/deliveryModes';

const getAddressSchema = (
    t: TFunction
): Yup.ObjectSchema<Record<keyof Address, Yup.AnySchema>> =>
    Yup.object({
        address1: Yup.string().required(t('ERRORS.REQUIRED')),
        address2: Yup.string().notRequired().nullable(),
        city: Yup.string().required(t('ERRORS.REQUIRED')),
        country: Yup.string().required(t('ERRORS.REQUIRED')),
        postcode: Yup.number()
            .typeError(t('ERRORS.REQUIRED'))
            .min(1000, t('ERRORS.POSTCODE_ONLY_FOUR_DIGIT'))
            .max(9999, t('ERRORS.POSTCODE_ONLY_FOUR_DIGIT'))
            .required(t('ERRORS.REQUIRED')),
        phone: Yup.string().matches(
            PATTERNS.PHONE_NUMBER,
            t('ERRORS.INVALID_PHONE_NUMBER')
        ),
    });

const getCourierServiceSchmea = (
    t: TFunction
): Yup.ObjectSchema<Record<keyof CourierService, Yup.AnySchema>> =>
    Yup.object({
        start_time: Yup.date()
            .required(t('ERRORS.REQUIRED'))
            .max(
                Yup.ref('end_time'),
                t('ERRORS.MUST_BE_SMALLER_THAN_END_TIME')
            ),
        end_time: Yup.date()
            .required(t('ERRORS.REQUIRED'))
            .min(
                Yup.ref('start_time'),
                t('ERRORS.MUST_BE_LARGER_THAN_START_TIME')
            )
            .test(
                'not-equal',
                t('ERRORS.MUST_BE_LARGER_THAN_START_TIME'),
                function (value: Date) {
                    const { start_time } = this.parent;
                    return dayjs(value).diff(dayjs(start_time)) !== 0;
                }
            ),
        delivery_address_id: Yup.number().nullable(),
        delivery_address: getAddressSchema(t),
    });

const refundProductItemSchema: Yup.ObjectSchema<
    Record<keyof RefundLineItem, Yup.AnySchema>
> = Yup.object({
    image: Yup.string(),
    itemNo: Yup.string(),
    description: Yup.string(),
    shippedQtyNotReturned: Yup.string(),
    quantityToReturn: Yup.number(),
    unitOfMeasureCode: Yup.string(),
    SourceNo: Yup.string(),
    documentType: Yup.string(),
    documentNo: Yup.string(),
    quantity: Yup.number(),
});

const getRefundSchema = (t: TFunction) =>
    Yup.object({
        mode: Yup.string().oneOf(DELIVERY_MODES).required(),
        products: Yup.array()
            .of(refundProductItemSchema)
            .min(1, t('ERRORS.MIN_PRODUCT')),
        reason: Yup.object().shape({
            is_other_reason: Yup.boolean(),
            id: Yup.number().when('is_other_reason', {
                is: true,
                then: Yup.number().nullable(),
                otherwise: Yup.number().required(t('ERRORS.REQUIRED')),
            }),
            text: Yup.string().when('is_other_reason', {
                is: true,
                then: Yup.string().required(t('ERRORS.REQUIRED')),
                otherwise: Yup.string().nullable(),
            }),
        }),
        courier_service: Yup.object().when('mode', {
            is: DeliveryMode.COURIER_SERVICE,
            then: getCourierServiceSchmea(t),
            otherwise: Yup.object(),
        }),
        transit: Yup.object().when('mode', {
            is: DeliveryMode.TRANSIT,
            then: getCourierServiceSchmea(t),
            otherwise: Yup.object(),
        }),
        pickup_point: Yup.object().when('mode', {
            is: DeliveryMode.PICKUP_POINT,
            then: Yup.object({
                store_location_id: Yup.number().required(t('ERRORS.REQUIRED')),
                phone: Yup.string().matches(
                    PATTERNS.PHONE_NUMBER,
                    t('ERRORS.INVALID_PHONE_NUMBER')
                ),
            }),
            otherwise: Yup.object(),
        }),
        package_parameters: Yup.array()
            .of(
                Yup.object().shape({
                    width: Yup.number()
                        .min(1, t('ERRORS.REQUIRED'))
                        .typeError(t('ERRORS.REQUIRED'))
                        .required(t('ERRORS.REQUIRED')),
                    height: Yup.number()
                        .min(1, t('ERRORS.REQUIRED'))
                        .typeError(t('ERRORS.REQUIRED'))
                        .required(t('ERRORS.REQUIRED')),
                    depth: Yup.number()
                        .min(1, t('ERRORS.REQUIRED'))
                        .typeError(t('ERRORS.REQUIRED'))
                        .required(t('ERRORS.REQUIRED')),
                    weight: Yup.number()
                        .min(1, t('ERRORS.REQUIRED'))
                        .typeError(t('ERRORS.REQUIRED'))
                        .required(t('ERRORS.REQUIRED')),
                    pallet_number: Yup.number()
                        .min(0, t('ERRORS.REQUIRED'))
                        .typeError(t('ERRORS.REQUIRED'))
                        .required(t('ERRORS.REQUIRED')),
                })
            )
            .min(0),
    });

export default getRefundSchema;
